<script lang="ts">
	import {
		Container,
		Modal,
		Text,
		Button,
		IconButton,
		Select,
		type SelectOptionPropsType,
		Token,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { thermostatsFilter, getSelectedThermostatFilterCount } from 'src/stores/ui/filters';
	import type { ThermostatsFilters } from 'src/stores/ui/filters';
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import { defaultSelectedRoom, defaultThermostatFilter } from 'src/helpers/constants';
	import { rooms } from 'src/stores/lists/rooms';
	import { sortBy } from 'src/components/Lists/list-helpers';

	export let openFilterModal;

	const thermostatOptions: SelectOptionPropsType[] = [
		{
			...defaultThermostatFilter,
			title: $format(defaultThermostatFilter.title),
		},
		{
			title: $format('select_thermostat_mode_option_heating'),
			value: 'heat',
		},
		{
			title: $format('select_thermostat_mode_option_cool'),
			value: 'cool',
		},
	];

	let appliedFilters: ThermostatsFilters = deepCopy($thermostatsFilter);

	$: orderedRooms = Object.values($rooms)
		.sort((x, y) => {
			return sortBy(x, y, 'position');
		})
		.map((room: any) => {
			return {
				title: room.name,
				value: room.id,
			};
		});

	$: roomsOptions = [
		{
			...defaultSelectedRoom,
			title: $format(defaultSelectedRoom.title, {
				default: defaultSelectedRoom.translationDefault,
			}),
		},
		...(orderedRooms && [...orderedRooms]),
	];

	// Find the selected room value from appliedFilters
	$: selectedRoomValue =
		appliedFilters.rooms.find((filter) => filter.value)?.value || defaultSelectedRoom.value;

	// Match it with the translated option from roomsOptions
	$: selectedRoom =
		roomsOptions.find((option) => option.value === selectedRoomValue) || roomsOptions[0];

	// Find the selected type value from appliedFilters
	$: selectedTypeValue =
		appliedFilters.type.find((filter) => filter.value)?.value || defaultThermostatFilter.value;

	// Match it with the translated option from thermostatOptions
	$: selectedType =
		thermostatOptions.find((option) => option.value === selectedTypeValue) || thermostatOptions[0];

	const handleFilter = () => {
		thermostatsFilter.set(appliedFilters);
		openFilterModal = false;
	};

	$: notification = getSelectedThermostatFilterCount(appliedFilters);

	function resetData() {
		appliedFilters = {
			rooms: [defaultSelectedRoom],
			type: [defaultThermostatFilter],
		};
		notification = getSelectedThermostatFilterCount(appliedFilters);
	}

	function handleRoomChange(e) {
		appliedFilters.rooms = [e.detail];
	}

	function handleTypeChange(e) {
		if (appliedFilters.type[0].value === e.detail.value) {
			appliedFilters.type = [defaultThermostatFilter];
			return;
		}

		appliedFilters.type = [e.detail];
	}
</script>

<Modal
	title={$format('modal_apply_groups_filters', {
		default: 'Filters',
	})}
	showModal={openFilterModal}
	on:close={() => {
		openFilterModal = false;
	}}
	backdropClose
	{notification}
	removeScrollPadding
>
	<Container fullHeight slot="content" gap="s" padding="scrollPaddings">
		<Text class="mb-s" element="h2">
			{$format('text_title_devices_filter_rooms', {
				default: 'Rooms',
			})}
		</Text>
		<Select
			class="width-sm-full width-md-l mb-m"
			id="select-room-filter-thermostats"
			aria-labelledby="select-room-filter-thermostats"
			value={selectedRoom}
			on:change={handleRoomChange}
			options={roomsOptions}
			icon="fas fa-door-open"
			label={$format('all_rooms', { default: 'All rooms' })}
		/>
		<Text class="mb-s" element="h2">
			{$format('choose_virtual_component_type_tab', {
				default: 'Type',
			})}
		</Text>
		{#each thermostatOptions as element, i}
			{#if i !== 0}
				<Token
					id="type-option-{i}"
					class="mr-s"
					selected={element.value === selectedType.value}
					title={element.title}
					size="m"
					on:click={() => {
						handleTypeChange({ detail: element });
					}}
				/>
			{/if}
		{/each}
	</Container>
	<div slot="footer" class="d-flex flex-direction-column">
		<IconButton
			on:click={resetData}
			icon="fas fa-xmark"
			variant="secondary"
			class="width-full"
			disabled={!notification}
		>
			{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
		</IconButton>
		<div class="d-flex justify-content-end mt-md-l mt-sm-m">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (openFilterModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleFilter}
			>
				{capFirstChar($format('button_apply', { default: 'Apply' }))}
			</Button>
		</div>
	</div>
</Modal>
